import { useTranslation } from 'react-i18next';
import { findSkillByEn, flattenRoles, resolveRoleLabel } from '@interviewgator/shared';

/** True for any Polish locale tag ('pl', 'pl-PL', …). */
function isPolish(language: string | undefined): boolean {
  return Boolean(language?.toLowerCase().startsWith('pl'));
}

/** Returns a function that picks the correct language variant of a static role title. */
export function useRoleTitle() {
  const { i18n } = useTranslation();
  return (role: { title: string; titlePl: string }): string =>
    isPolish(i18n.language) ? role.titlePl : role.title;
}

/** Returns a function that picks the correct language variant of a saved/extracted role. */
export function useSavedRoleTitle() {
  const { i18n } = useTranslation();
  return (role: { roleEn: string; rolePl: string }): string =>
    isPolish(i18n.language) ? role.rolePl || role.roleEn : role.roleEn;
}

/** Returns a function that picks the correct language variant of a skill pair. */
export function useSkillLabel() {
  const { i18n } = useTranslation();
  return (skill: { en: string; pl: string }): string => (isPolish(i18n.language) ? skill.pl : skill.en);
}

/**
 * Localizes a persisted role — a simulation, company template, invite or
 * analytics row carrying `{ role, rolePl }`. `role` is English-canonical
 * everywhere; `rolePl` is the translation captured when the row was created,
 * with the static catalog as a fallback for older rows.
 */
export function useLocalizedRole() {
  const { i18n } = useTranslation();
  return (source: { role?: string | null; rolePl?: string | null } | null | undefined): string =>
    resolveRoleLabel(source, i18n.language);
}

/** Resolves an English-canonical role string to a display label (static list, saved roles, or fallback). */
export function useRoleDisplayLabel() {
  const { i18n } = useTranslation();
  return (roleEn: string | undefined, savedRoles?: Array<{ roleEn: string; rolePl: string }>): string => {
    if (!roleEn) return '';
    const savedMatch = savedRoles?.find((r) => r.roleEn === roleEn);
    if (savedMatch) {
      return isPolish(i18n.language) ? savedMatch.rolePl || savedMatch.roleEn : savedMatch.roleEn;
    }
    const staticMatch = flattenRoles().find((r) => r.title === roleEn);
    if (staticMatch) {
      return isPolish(i18n.language) ? staticMatch.titlePl : staticMatch.title;
    }
    return roleEn;
  };
}

/** Resolves an English-canonical skill value to a display label (static list or fallback). */
export function useSkillDisplayLabel() {
  const { i18n } = useTranslation();
  return (en: string, savedSkill?: { en: string; pl: string }): string => {
    if (savedSkill && savedSkill.en === en) {
      return isPolish(i18n.language) ? savedSkill.pl : savedSkill.en;
    }
    const staticSkill = findSkillByEn(en);
    if (staticSkill) {
      return isPolish(i18n.language) ? staticSkill.pl : staticSkill.en;
    }
    return en;
  };
}
